import { useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { api } from '../../shared/api/client';
import type { BibleBook, BiblePassage } from '../../shared/api/types';

/** Sin acentos ni mayúsculas, para comparar lo que se escribe con el nombre del libro. */
export const plainBook = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Libros que empiezan con lo escrito, en orden canónico («ex» → Éxodo, Exequiel no existe). */
export const bookMatches = (books: readonly BibleBook[], typed: string) => (typed ? books.filter((b) => plainBook(b.name).startsWith(plainBook(typed))) : []);

export type RefState = { stage: 'book' | 'chapter' | 'verse'; typed: string; book: BibleBook | null; chapter: string; verse: string };
export const EMPTY_REF: RefState = { stage: 'book', typed: '', book: null, chapter: '', verse: '' };

/** Texto que se ve en el campo y la parte sugerida (seleccionada) del nombre del libro. */
export function refText(s: RefState, books: readonly BibleBook[]): { text: string; selectFrom: number | null } {
  if (s.stage === 'book') {
    const m = bookMatches(books, s.typed)[0];
    // Lo escrito con el nombre bien escrito del libro; lo que falta va seleccionado.
    return m && m.name.length > s.typed.length ? { text: m.name, selectFrom: s.typed.length } : { text: m ? m.name : s.typed, selectFrom: null };
  }
  if (s.stage === 'chapter') return { text: `${s.book!.name} ${s.chapter}`, selectFrom: null };
  return { text: `${s.book!.name} ${s.chapter}:${s.verse}`, selectFrom: null };
}

/**
 * Una tecla en el buscador de citas (como EasyWorship): completa el libro mientras se escribe e
 * ignora lo que no lleva a ningún libro; espacio, Tab o Enter aceptan el libro y pasan al capítulo;
 * espacio o «:» pasan al versículo; los números fuera de rango no entran; retroceso deshace por partes.
 * Devuelve el estado nuevo, o null si la tecla no cambia nada (se ignora).
 */
export function refKey(s: RefState, key: string, books: readonly BibleBook[], maxVerse: number | null): RefState | null {
  if (key === 'Backspace') {
    if (s.stage === 'verse') return s.verse ? { ...s, verse: s.verse.slice(0, -1) } : { ...s, stage: 'chapter' };
    if (s.stage === 'chapter') return s.chapter ? { ...s, chapter: s.chapter.slice(0, -1) } : { ...EMPTY_REF, typed: s.book!.name };
    return s.typed ? { ...EMPTY_REF, typed: s.typed.slice(0, -1) } : null;
  }
  const accept = key === ' ' || key === 'Tab' || key === 'Enter';
  if (s.stage === 'book') {
    const matches = bookMatches(books, s.typed);
    // «1 co»: después de un número el espacio es parte del nombre, no acepta el libro.
    if (key === ' ' && /^\d$/.test(s.typed.trim()) && bookMatches(books, `${s.typed} `).length > 0) return { ...s, typed: `${s.typed} ` };
    if (accept) return matches[0] ? { ...EMPTY_REF, stage: 'chapter', book: matches[0] } : null;
    if (key.length !== 1) return null;
    const next = s.typed + key;
    return bookMatches(books, next).length > 0 ? { ...s, typed: next } : null;
  }
  if (s.stage === 'chapter') {
    if (/^\d$/.test(key)) {
      const n = Number(s.chapter + key);
      return n >= 1 && n <= s.book!.chapters ? { ...s, chapter: String(n) } : null;
    }
    if ((key === ' ' || key === ':' || key === 'Tab') && s.chapter) return { ...s, stage: 'verse' };
    return null;
  }
  if (/^\d$/.test(key)) {
    const n = Number(s.verse + key);
    return n >= 1 && (maxVerse === null || n <= maxVerse) ? { ...s, verse: String(n) } : null;
  }
  return null;
}

/** La cita para buscar: sin versículo, el capítulo desde el versículo 1. */
export const refQuery = (s: RefState) => (s.book && s.chapter ? `${s.book.name} ${s.chapter}:${s.verse || '1'}` : s.book ? `${s.book.name} 1:1` : null);

/** Campo de cita con autocompletado del libro (Palabra de la consola). Enter busca. */
export function BibleRefInput({ id, onSearch, onQuery }: { id: string; onSearch: (ref: string) => void; onQuery?: (ref: string | null) => void }) {
  const [books, setBooks] = useState<BibleBook[]>([]);
  const [s, setS] = useState<RefState>(EMPTY_REF);
  const [maxVerse, setMaxVerse] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void api
      .get<{ items: BibleBook[] }>('/bible/books')
      .then((r) => setBooks(r.items))
      .catch(() => undefined);
  }, []);

  // Al pasar al versículo se sabe cuántos tiene ese capítulo.
  useEffect(() => {
    setMaxVerse(null);
    if (s.stage !== 'verse' || !s.book) return;
    let alive = true;
    void api
      .get<BiblePassage>(`/bible/passage?ref=${encodeURIComponent(`${s.book.name} ${s.chapter}`)}`)
      .then((p) => alive && setMaxVerse(p.verses.length > 0 ? Math.max(...p.verses.map((v) => v.verse)) : null))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [s.stage, s.book, s.chapter]);

  const query = refQuery(s);
  useEffect(() => onQuery?.(query), [query, onQuery]);

  const { text, selectFrom } = refText(s, books);
  useLayoutEffect(() => {
    const el = input.current;
    if (!el || document.activeElement !== el) return;
    if (selectFrom !== null) el.setSelectionRange(selectFrom, text.length);
    else el.setSelectionRange(text.length, text.length);
  }, [text, selectFrom]);

  function key(e: KeyboardEvent<HTMLInputElement>) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setS(EMPTY_REF);
      return;
    }
    if (e.key === 'Enter' && s.stage !== 'book') {
      e.preventDefault();
      const q = refQuery(s);
      if (q) onSearch(q);
      return;
    }
    if (e.key === 'Enter' && s.stage === 'book' && !s.typed) return;
    // Tab sin nada que aceptar deja salir del campo.
    if (e.key === 'Tab' && (s.stage === 'verse' || (s.stage === 'book' && !bookMatches(books, s.typed)[0]) || (s.stage === 'chapter' && !s.chapter))) return;
    if (e.key.length !== 1 && !['Backspace', 'Tab', 'Enter'].includes(e.key)) return;
    e.preventDefault();
    const next = refKey(s, e.key, books, maxVerse);
    if (next) {
      setS(next);
      // Enter sobre un libro: lo acepta y busca su capítulo 1 si no hay más.
      if (e.key === 'Enter' && next.stage === 'chapter') onSearch(`${next.book!.name} 1:1`);
    }
  }

  /** Pegar «Juan 3:16»: se toma si el libro existe. */
  function paste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const raw = e.clipboardData.getData('text').trim();
    const m = /^(.+?)\s+(\d{1,3})(?:\s*[:.]\s*(\d{1,3}))?/.exec(raw);
    const book = m ? books.find((b) => plainBook(b.name) === plainBook(m[1]!)) ?? bookMatches(books, m[1]!)[0] : null;
    if (!m || !book) return;
    const chapter = Math.min(Number(m[2]), book.chapters);
    setS({ stage: m[3] ? 'verse' : 'chapter', typed: '', book, chapter: String(chapter), verse: m[3] ?? '' });
  }

  return (
    <input
      id={id}
      ref={input}
      value={text}
      placeholder="Escribe el libro: ex → Éxodo, espacio, capítulo, espacio, versículo"
      autoComplete="off"
      spellCheck={false}
      onKeyDown={key}
      onPaste={paste}
      onChange={() => undefined}
    />
  );
}
